import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Scale, Users, TrendingUp, Activity, Droplet, Folder, ClipboardCheck, Phone, Mail, MapPin, Facebook, Play, ChevronRight, ChevronLeft, UserPlus, FileText, Calculator, RefreshCw, Send, CheckCircle, Download, Share2, MessageCircle, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import CallMeBackButton from '../components/CallMeBackButton';

// Interactive Demo Tour Component
const DemoTour = ({ isOpen, onClose, onStartPlanning }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Mock pricing breakdown component for demo
  const PricingBreakdownMockup = () => (
    <div className="bg-gradient-to-r from-[#ff6b00]/10 to-transparent border-l-4 border-[#ff6b00] rounded-r-xl p-4 space-y-2 text-sm">
      <div className="text-center mb-2">
        <p className="text-[#1a4d2e] font-bold">Your Custom PriceHub Package</p>
      </div>
      <div className="flex justify-between">
        <span className="text-[#4a4a4a]">Liveweight</span>
        <span className="font-mono font-semibold text-[#1a4d2e]">$622.50</span>
      </div>
      <div className="flex justify-between">
        <span className="text-[#4a4a4a]">Herd Read</span>
        <span className="font-mono font-semibold text-[#1a4d2e]">$472.50</span>
      </div>
      <div className="flex justify-between bg-green-50 p-1 rounded border border-green-200">
        <span className="text-green-700 font-medium">🎉 Multi-Service Discount</span>
        <span className="font-mono font-semibold text-green-700">-$120.00</span>
      </div>
      <div className="flex justify-between pt-2 border-t border-[#e5ddd5]">
        <span className="text-[#4a4a4a] font-medium">Total (excl. GST)</span>
        <span className="font-mono font-semibold text-[#1a4d2e]">$1,147.00</span>
      </div>
      <div className="flex justify-between">
        <span className="text-[#4a4a4a]">GST (15%)</span>
        <span className="font-mono font-semibold text-[#1a4d2e]">$130.35</span>
      </div>
    </div>
  );

  // Mock total price component for demo
  const TotalPriceMockup = () => (
    <div className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] rounded-lg p-4 shadow-lg">
      <div className="text-center mb-1">
        <span className="text-white text-xs uppercase tracking-wider font-semibold">
          Your Total Price (excl. GST)
        </span>
      </div>
      <div className="flex justify-center items-center">
        <span className="font-mono text-3xl font-bold text-white">
          $869.00
        </span>
      </div>
      <div className="text-center mt-1">
        <span className="text-white/90 text-sm font-medium">
          + GST - No Hidden Costs
        </span>
      </div>
    </div>
  );

  const tourSteps = [
    {
      title: "Welcome to PriceHub",
      subtitle: "Your Interactive Pricing Tool",
      icon: Calculator,
      description: "PriceHub lets you select one or more services and see exactly what it costs - instantly. No waiting, no surprises.",
      highlight: "See real-time pricing as you select services",
      color: "from-[#ff6b00] to-[#ff8533]"
    },
    {
      title: "Step 1: Quick Registration",
      subtitle: "30 seconds to get started",
      icon: UserPlus,
      description: "Enter your basic details - name, farm, email, and phone. This helps us provide personalized service recommendations.",
      highlight: "Your information is secure and never shared",
      color: "from-[#1a4d2e] to-[#2d6b4a]"
    },
    {
      title: "Step 2: Your Contact Details",
      subtitle: "Tell us where and when",
      icon: FileText,
      description: "Add your farm address, preferred date, and time. We'll work around your schedule to find the best time for your visit.",
      highlight: "We work with YOUR schedule",
      color: "from-[#8d6e63] to-[#a1887f]"
    },
    {
      title: "Step 3: Select Your Services",
      subtitle: "Select one or more services",
      icon: CheckCircle,
      description: "Choose from our range of services - Liveweight, Herd Read, (DNA) Tissue Sampling, BCS, and more. Enter the number of animals for each service.",
      highlight: "Mix and match services to suit your needs",
      color: "from-[#ff6b00] to-[#ff8533]"
    },
    {
      title: "Step 4: See Instant Pricing",
      subtitle: "No waiting, no guessing",
      icon: Calculator,
      description: "Watch your total update in real-time as you add or remove services. Here's what your pricing breakdown looks like:",
      highlight: "Multi-service discounts applied automatically",
      color: "from-[#1a4d2e] to-[#2d6b4a]",
      showPricingBreakdown: true
    },
    {
      title: "Your Total - Clear & Simple",
      subtitle: "GST Inclusive - No Surprises",
      icon: Calculator,
      description: "Your final price is displayed prominently so you know exactly what to expect. Everything is included - no hidden fees!",
      highlight: "What you see is what you pay",
      color: "from-[#ff6b00] to-[#ff8533]",
      showTotalPrice: true
    },
    {
      title: "Step 5: Revise & Submit",
      subtitle: "You're in control",
      icon: RefreshCw,
      description: "Not quite right? Easily revise your selections and see the new price instantly. When you're happy, submit your request and we'll be in touch.",
      highlight: "Change your mind anytime before submitting",
      color: "from-[#8d6e63] to-[#a1887f]"
    },
    {
      title: "Ready to Get Started?",
      subtitle: "It only takes a few minutes",
      icon: Send,
      description: "Join hundreds of farmers who use PriceHub to plan their livestock services. Quick, easy, and transparent pricing.",
      highlight: "Start planning your day now!",
      color: "from-[#ff6b00] to-[#ff8533]",
      isFinal: true
    }
  ];

  const currentTourStep = tourSteps[currentStep];
  const IconComponent = currentTourStep.icon;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const handleStartPlanning = () => {
    setCurrentStep(0);
    onStartPlanning();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header with gradient */}
            <div className={`bg-gradient-to-r ${currentTourStep.color} p-6 text-white`}>
              <div className="flex items-center gap-4">
                <div className="bg-white/20 rounded-full p-3">
                  <IconComponent className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-heading">{currentTourStep.title}</h3>
                  <p className="text-white/90 text-sm">{currentTourStep.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-[#4a4a4a] leading-relaxed">
                {currentTourStep.description}
              </p>
              
              {/* Show pricing breakdown mockup */}
              {currentTourStep.showPricingBreakdown && (
                <PricingBreakdownMockup />
              )}

              {/* Show total price mockup */}
              {currentTourStep.showTotalPrice && (
                <TotalPriceMockup />
              )}
              
              <div className="bg-[#fff8f0] border-l-4 border-[#ff6b00] p-3 rounded-r">
                <p className="text-sm font-medium text-[#1a4d2e]">
                  ✨ {currentTourStep.highlight}
                </p>
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-2 pt-2">
                {tourSteps.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentStep 
                        ? 'bg-[#ff6b00] w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Footer buttons */}
            <div className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="text-[#4a4a4a]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>

              <span className="text-sm text-gray-500">
                {currentStep + 1} of {tourSteps.length}
              </span>

              {currentTourStep.isFinal ? (
                <Button
                  onClick={handleStartPlanning}
                  className="bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white"
                >
                  Start Planning Now
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    // Set page title for browser tab
    document.title = 'Measured Livestock';
  }, []);

  const services = [
    {
      icon: Scale,
      title: 'Liveweight',
      shortDescription: 'Weighing young stock to record accurate liveweights and measure early growth performance',
      items: ['All weighing includes Animal ID verification', 'Dairy Heifer, Whole Herd, Pre & Post Grazing'],
      fullDescription: {
        subtitle: 'Liveweight Recording',
        whatThisIs: 'Weighing young stock to record accurate liveweights and measure early growth performance, helping identify how animals are tracking and supporting better management and breeding decisions.',
        howItWorks: [
          'We book a visit with you and confirm timing the day before.',
          'We arrive with calibrated weighing and EID scanning equipment.',
          'As cattle move through the yards we:',
          '• Verify each animal\'s ID against your herd records',
          '• Replace missing EID tags where required',
          '• Record and report any tag or record differences',
          '• Weigh each animal and record the data electronically',
          'After the visit, all data is checked and uploaded to your herd recording system (or shared with you if requested).'
        ],
        whatWeNeed: [
          'Safe, workable yards and steady cattle flow.',
          'At least one person to help handle stock.',
          'Replacement EID tags available if required.',
          'Availability at the booked time.'
        ]
      }
    },
    {
      icon: Droplet,
      title: '(DNA) Tissue Sampling',
      shortDescription: 'Accurate collection for parentage and genomics, includes Animal ID verification',
      description: 'Accurate collection for parentage and genomics, includes Animal ID verification',
      fullDescription: {
        subtitle: 'DNA / Tissue Sampling',
        note: 'For DNA sampling, animal verification, tag replacement, and variance reporting are always part of the process, regardless of which option you choose.',
        whatThisIs: 'We manage the entire DNA process on-farm, start to finish.',
        howItWorks: [
          'We book a time that suits you.',
          'We bring all equipment and herd data.',
          'On-farm we:',
          '• Verify every animal\'s physical ID',
          '• Replace missing EID tags',
          '• Record and report any mismatches or unknown animals',
          '• Collect tissue samples and electronically link them to each animal',
          'Samples are sent to the lab and data uploaded after the visit.'
        ],
        whatWeNeed: [
          'Yard access and help moving cattle.',
          'Replacement EID tags available.',
          'Access to herd records.'
        ]
      }
    },
    {
      icon: ClipboardCheck,
      title: 'Herd Read',
      shortDescription: 'Compare physical animal data with Industry/farm records, includes verification for myHerd, Minda and NAIT',
      description: 'Compare physical animal data with Industry/farm records, includes verification for myHerd, Minda and NAIT',
      fullDescription: {
        whatThisIs: 'A full scan of your herd to confirm animal numbers and record accuracy.',
        howItWorks: [
          'Every animal is scanned through the yards.',
          'We:',
          '• Verify each animal against herd records',
          '• Replace missing EID tags',
          '• Identify extra, missing, or mismatched animals',
          '• Provide a clear variance report',
          'You receive a summary showing exactly what needs updating.'
        ],
        whatWeNeed: [
          'Access to the full herd.',
          'Time to run all animals through the yards.',
          'Replacement EID tags available.'
        ]
      }
    },
    {
      icon: Activity,
      title: 'BCS (Body Condition Score)',
      shortDescription: 'Visual assessment of body condition to support feeding, breeding, and management decisions',
      description: 'Visual assessment of body condition to support feeding, breeding, and management decisions',
      logo: '/images/bcs-certified-logo.jpg',
      fullDescription: {
        subtitle: 'Body Condition Scoring',
        whatThisIs: 'Body Condition Scoring (BCS) is a hands-on, visual assessment of an animal\'s body fat reserves. It provides a practical measure of whether cattle are in the right condition for their stage of production — whether that\'s mating, calving, drying off, or finishing. BCS helps farmers make timely feeding, management, and breeding decisions based on what they can see and feel, rather than guesswork.',
        howItWorks: [
          'We book a visit with you at a time that suits your farm calendar.',
          'As cattle move through the yards, each animal is:',
          '• Scanned and identified using EID',
          '• Visually assessed and scored on the standard 1–10 BCS scale',
          '• Any ID issues (missing tags, mismatches) are noted and reported',
          'After the visit, results are compiled and shared with you in a clear, usable format — either uploaded to your herd recording platform or provided directly.'
        ],
        whatWeNeed: [
          'Safe, functional yards with steady cattle flow.',
          'At least one person available to help move stock.',
          'Replacement EID tags on hand if needed.',
          'Availability at the agreed time.'
        ]
      }
    },
    {
      icon: Folder,
      title: 'Special Projects',
      shortDescription: 'Tailored field projects and custom animal data collection',
      description: 'Tailored field projects and custom animal data collection',
      fullDescription: {
        whatThisIs: 'Custom on-farm work such as targeted data collection, research or breed projects, and one-off farm requirements.',
        howItWorks: [
          'Scope and timing agreed with you.',
          'Data and samples collected as required.',
          'Results provided as agreed.'
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-[#f4f4f0]">
      {/* Hero Section */}
      <div 
        className="relative h-[90vh] bg-cover bg-center grain-texture"
        style={{
          backgroundImage: 'linear-gradient(rgba(26, 77, 46, 0.55), rgba(26, 77, 46, 0.65)), url(https://images.unsplash.com/photo-1709733971576-8caa9df3abf9)',
        }}
      >
        <div className="container mx-auto px-4 h-full flex flex-col justify-center items-center text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              Measured Livestock
            </h1>
            <p className="text-xl md:text-2xl mb-2 font-medium">
              Trait Collection Service
            </p>
            <p className="text-2xl md:text-3xl mb-3 text-[#ff6b00] font-extrabold tracking-wide">
              Every Animal Checked & Verified
            </p>
            <p className="text-lg md:text-xl mb-2 font-medium text-white/95">
              We work alongside you — lining up with your existing data
            </p>
            <p className="text-base md:text-lg mb-8 opacity-90">
              Liveweight • DNA Sampling • Herd Read • Body Condition Score
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                className="bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-lg px-8 py-8 h-auto rounded-full font-heading shadow-xl hover:shadow-2xl transition-all flex items-center gap-3 hover:scale-105 mx-auto"
                onClick={() => navigate('/book')}
                data-testid="hero-get-quote-button"
              >
                <span className="bg-white/20 p-3 rounded-full">
                  <Play className="w-6 h-6 fill-current" />
                </span>
                Plan Your Day - Quick & Easy
              </Button>
            </div>
            <p className="text-base md:text-lg mt-4 font-semibold text-white/95">
              ⚡ Keeping Pricing Simple, Smart - No Surprises - We work with your schedule
            </p>
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <div className="container mx-auto px-4 py-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-center mb-4 text-[#1a4d2e]">
            Livestock Services for Dairy & Beef Farms
          </h2>
          <p className="text-center text-lg text-[#8d6e63] mb-16">
            Professional, mobile, and geared to support compliance, growth, and day-to-day farm management
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <Card 
                  className="border border-[#e5ddd5] hover:border-[#1a4d2e] transition-all h-full cursor-pointer"
                  onClick={() => setSelectedService(service)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <service.icon className="w-12 h-12 text-[#ff6b00]" strokeWidth={1.5} />
                      {service.logo && (
                        <img src={service.logo} alt="Certified" className="w-16 h-16 object-contain rounded-full" />
                      )}
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-3 text-[#1a4d2e]">
                      {service.title}
                    </h3>
                    {service.items ? (
                      <ul className="space-y-2 text-[#4a4a4a]">
                        {service.items.map((item, i) => (
                          <li key={i} className="text-sm">• {item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[#4a4a4a] text-sm">{service.description}</p>
                    )}
                    <p className="text-[#ff6b00] text-sm font-medium mt-4">Click for more details →</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Farmer Services Brochure — prominent download/share card */}
      <div className="bg-gradient-to-br from-[#fff8f0] via-[#f4f4f0] to-[#fff8f0] py-12 border-y border-[#e5ddd5]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-xl border-2 border-[#ff6b00]/30 overflow-hidden">
              <div className="grid md:grid-cols-3 gap-0">
                {/* Left: icon panel */}
                <div className="bg-gradient-to-br from-[#1a4d2e] to-[#2d6b47] text-white p-8 flex flex-col justify-center items-center text-center md:col-span-1">
                  <div className="bg-white/15 rounded-full p-5 mb-4">
                    <FileText className="w-14 h-14 text-[#ff6b00]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-1">Services Info Sheet</h3>
                  <p className="text-white/85 text-sm">A clear 2-page PDF — everything farmers need to know.</p>
                </div>

                {/* Right: content + actions */}
                <div className="p-6 md:p-8 md:col-span-2">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1a4d2e] mb-3">
                    Take Our Services Info With You
                  </h2>
                  <p className="text-[#4a4a4a] mb-5 text-sm md:text-base">
                    Download our farmer-friendly PDF — covering each service, what to have ready before the visit,
                    and a QR code that opens PriceHub on your phone. Perfect for sharing with staff, partners or
                    sending to a farmer who's still deciding.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/farmer-services-pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white px-5 py-3 rounded-lg font-heading font-semibold transition-all shadow-md hover:shadow-lg"
                      data-testid="services-pdf-download-btn"
                    >
                      <Download className="w-5 h-5" />
                      Download PDF
                    </a>

                    <a
                      href={`mailto:?subject=${encodeURIComponent('Measured Livestock — Services Info')}&body=${encodeURIComponent(`Hi,\n\nThought you'd find this useful — a quick overview of Measured Livestock's services and how to get instant pricing.\n\nDownload the info sheet here:\nhttps://measuredlivestock.com/api/marketing/farmer-services-pdf\n\nOr browse pricing direct on the PriceHub:\nhttps://measuredlivestock.com/book\n\nCheers`)}`}
                      className="flex items-center justify-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-5 py-3 rounded-lg font-heading font-semibold transition-all shadow-md hover:shadow-lg"
                      data-testid="services-pdf-email-btn"
                    >
                      <Mail className="w-5 h-5" />
                      Email to a Farmer
                    </a>

                    <a
                      href={`https://wa.me/?text=${encodeURIComponent("Measured Livestock services & instant pricing — have a look: https://measuredlivestock.com/api/marketing/farmer-services-pdf")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-white border-2 border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e]/5 px-5 py-3 rounded-lg font-heading font-semibold transition-all"
                      data-testid="services-pdf-whatsapp-btn"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Share via WhatsApp
                    </a>

                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/api/marketing/farmer-services-pdf`;
                        navigator.clipboard.writeText(url).then(() => {
                          // Lightweight visual feedback without bringing in toast on this page
                          const btn = document.querySelector('[data-testid="services-pdf-copy-link-btn"]');
                          if (btn) {
                            const orig = btn.innerHTML;
                            btn.innerHTML = '<span class="text-sm">✓ Link copied!</span>';
                            setTimeout(() => { btn.innerHTML = orig; }, 1800);
                          }
                        });
                      }}
                      className="flex items-center justify-center gap-2 bg-white border-2 border-[#ff6b00] text-[#ff6b00] hover:bg-[#ff6b00]/5 px-5 py-3 rounded-lg font-heading font-semibold transition-all"
                      data-testid="services-pdf-copy-link-btn"
                    >
                      <Copy className="w-5 h-5" />
                      Copy Link
                    </button>
                  </div>

                  <p className="text-xs text-[#8d6e63] mt-4 flex items-center gap-1.5">
                    <Share2 className="w-3.5 h-3.5" />
                    Free to share — printable on A4 — includes QR code to PriceHub
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-[#1a4d2e] text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-12">
              Why Farmers Choose Measured Livestock
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2 text-[#ff6b00]">Friendly, experienced technicians</h3>
                <p className="opacity-90">Our team brings expertise and a personal touch to every visit</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2 text-[#ff6b00]">Practical, no-fuss service</h3>
                <p className="opacity-90">We understand farm life and work around your schedule</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2 text-[#ff6b00]">Accurate & compliant data</h3>
                <p className="opacity-90">Industry-standard collection methods ensure reliable results</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2 text-[#ff6b00]">Solutions that grow with you</h3>
                <p className="opacity-90">From startups to large operations, we scale to your needs</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#8d6e63] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Let's get started!
            </h2>
            <p className="text-xl mb-3 font-semibold">
              Select one or more services • See prices instantly • No surprises
            </p>
            <p className="text-base mb-10 opacity-90">
              ⚡ Interactive pricing that updates as you select services
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                size="lg"
                className="bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-lg px-12 py-6 h-auto rounded-md font-heading"
                onClick={() => navigate('/book')}
                data-testid="cta-get-quote-button"
              >
                Start Planning Now
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#8d6e63] text-lg px-12 py-6 h-auto rounded-md font-heading"
                onClick={() => navigate('/login')}
                data-testid="cta-login-button"
              >
                Login
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1c1c1c] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Company Info */}
            <div className="text-center md:text-left">
              <h3 className="font-heading text-xl font-bold mb-4 text-[#ff6b00]">Measured Livestock</h3>
              <p className="text-sm opacity-90">Trait Collection Service</p>
              <p className="text-sm opacity-70 mt-2">Reliable • Accurate • Farmer-Friendly</p>
            </div>
            
            {/* Contact Info */}
            <div className="text-center">
              <h3 className="font-heading text-xl font-bold mb-4 text-[#ff6b00]">Contact Us</h3>
              <div className="space-y-3">
                <a href="tel:+6421327944" className="flex items-center justify-center gap-2 text-sm hover:text-[#ff6b00] transition-colors">
                  <Phone className="w-4 h-4" />
                  +64 21 327 944
                </a>
                <a href="mailto:info@measuredlivestock.co.nz" className="flex items-center justify-center gap-2 text-sm hover:text-[#ff6b00] transition-colors">
                  <Mail className="w-4 h-4" />
                  info@measuredlivestock.co.nz
                </a>
                <div className="flex items-center justify-center gap-2 text-sm opacity-90">
                  <MapPin className="w-4 h-4" />
                  Feilding, Manawatu
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="text-center md:text-right">
              <h3 className="font-heading text-xl font-bold mb-4 text-[#ff6b00]">Follow Us</h3>
              <a 
                href="https://www.facebook.com/profile.php?id=61585561673587" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm hover:text-[#ff6b00] transition-colors"
              >
                <Facebook className="w-5 h-5" />
                Measured Livestock
              </a>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-white/20 pt-6 pb-24 md:pb-6">
            <div className="flex flex-col items-center md:flex-row md:justify-between gap-4 text-center md:text-left">
              <p className="text-sm opacity-70">© 2024 Measured Livestock - Trait Collection Service. All rights reserved.</p>

              {/* Powered by Arvo */}
              <a
                href="https://www.getarvo.co.nz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-baseline gap-1 hover:opacity-80 transition-opacity"
                data-testid="powered-by-arvo-link"
                aria-label="Visit getarvo.co.nz"
              >
                <span className="text-[10px] text-white/40 uppercase tracking-widest">Powered by</span>
                <span className="text-lg font-bold text-[#ff6b00]/80 tracking-wide">arvo</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Service Details Modal */}
      <Dialog open={selectedService !== null} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-heading text-[#1a4d2e] flex items-center gap-3">
                  <selectedService.icon className="w-8 h-8 text-[#ff6b00]" strokeWidth={1.5} />
                  {selectedService.title}
                  {selectedService.fullDescription.subtitle && (
                    <span className="text-xl text-[#8d6e63]">
                      ({selectedService.fullDescription.subtitle})
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 text-[#4a4a4a]">
                {selectedService.fullDescription.note && (
                  <div className="bg-[#fff8f0] border-l-4 border-[#ff6b00] p-4 rounded">
                    <p className="text-sm italic">{selectedService.fullDescription.note}</p>
                  </div>
                )}

                {selectedService.fullDescription.whatThisIs && (
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-[#1a4d2e] mb-2">
                      What this is
                    </h3>
                    <p className="leading-relaxed">{selectedService.fullDescription.whatThisIs}</p>
                  </div>
                )}

                {selectedService.fullDescription.howItWorks && (
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-[#1a4d2e] mb-2">
                      How it works
                    </h3>
                    <ul className="space-y-2">
                      {selectedService.fullDescription.howItWorks.map((step, idx) => (
                        <li key={idx} className={step.startsWith('•') ? 'ml-6' : ''}>
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedService.fullDescription.whatWeNeed && (
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-[#1a4d2e] mb-2">
                      What we need from you
                    </h3>
                    <ul className="space-y-2">
                      {selectedService.fullDescription.whatWeNeed.map((need, idx) => (
                        <li key={idx}>• {need}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-4 border-t border-[#e5ddd5]">
                  {selectedService.title === 'Special Projects' ? (
                    <div className="text-center space-y-4">
                      <p className="text-[#4a4a4a]">
                        Special Projects require custom planning and pricing. Please contact us directly to discuss your requirements.
                      </p>
                      <Button 
                        className="w-full bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white"
                        onClick={() => {
                          setSelectedService(null);
                          window.location.href = 'mailto:info@measuredlivestock.co.nz?subject=Special Projects Inquiry';
                        }}
                      >
                        Contact Us Directly
                      </Button>
                    </div>
                  ) : selectedService.title === 'BCS (Body Condition Score)' ? (
                    <Button 
                      className="w-full bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white"
                      onClick={() => {
                        setSelectedService(null);
                        navigate('/book');
                      }}
                    >
                      Plan Your Day with BCS
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white"
                      onClick={() => {
                        setSelectedService(null);
                        navigate('/book');
                      }}
                    >
                      Plan Your Day with {selectedService.title}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Call Me Back Floating Button */}
      <CallMeBackButton />
    </div>
  );
};

export default LandingPage;
